import * as Sentry from "@sentry/react";

import { isProduction, isStaging } from "./environment";

type AppContext =
  | "get-help"
  | "contact-form"
  | "copilot"
  | "api-route"
  | "support-chat"
  | "global"
  | "unknown";

interface LogErrorContext {
  app?: AppContext;
  feature?: string;
  extra?: Record<string, unknown>;
  user?: {
    id?: string;
    email?: string;
  };
}

/**
 * Centralized error logging utility for the support frontend.
 *
 * In production/staging: Logs to Sentry with tags and breadcrumbs
 * In development: Logs to console with formatted output
 *
 * @param message - Human-readable description of the error
 * @param error - The error object (if available)
 * @param context - Additional context for debugging
 *
 * @example
 * ```ts
 * logError("Failed to submit contact form", error, {
 *   app: "contact-form",
 *   feature: "submission",
 *   extra: { category: "billing", retryCount: 3 }
 * });
 * ```
 */
export function logError(
  message: string,
  error?: unknown,
  context?: LogErrorContext,
): void {
  const tags = {
    app: context?.app || "unknown",
    feature: context?.feature,
  };

  if (isProduction || isStaging) {
    Sentry.withScope((scope) => {
      scope.setTags(tags);

      if (context?.user) {
        scope.setUser(context.user);
      }

      if (context?.extra) {
        // Use app as context name (matches AppContext type)
        scope.setContext(tags.app, context.extra);
      }

      // Add breadcrumb for context
      if (context) {
        Sentry.addBreadcrumb({
          message,
          level: "error",
          data: context.extra,
        });
      }

      // For ErrorBoundary errors, send message separately for easier Sentry searching
      if (
        message.includes("ErrorBoundary") ||
        message.includes("error boundary")
      ) {
        Sentry.captureMessage(message);
      }

      // Capture the error or message
      if (error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message);
      }
    });
  } else {
    // Development: log to console with structured format
    const prefix = `[${tags.app}${tags.feature ? `/${tags.feature}` : ""}]`;
    if (error) {
      console.error(prefix, message, error, context?.extra);
    } else {
      console.error(prefix, message, context?.extra);
    }
  }
}
